import React from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Chip,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import EditPermissionContext from 'contexts/EditPermissionContext';
import AppContext from 'contexts/AppContext';
import Permission from 'types/models/Permission';
import PaginationData from 'types/models/PaginationData';
import { getPermissionsWithPagination } from 'services/permissionServices';
import SearchBar from 'components/SearchBar';
import useDebounce from 'hooks/useDebounce';

export default (): JSX.Element => {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentPageListNumber, setCurrentPageListNumber } = React.useContext(AppContext);
  const [paginationPermission, setPaginationPermission] = React.useState<PaginationData<Permission>>();
  const [searchValue, setSearchValue] = React.useState('');
  const { setSelectedPermission } = React.useContext(EditPermissionContext);

  const loadPermissions = React.useCallback(async (page?: number, searchTerm?: string) => {
    try {
      const paginationData = await getPermissionsWithPagination(page, searchTerm);
      if (paginationData) {
        setPaginationPermission(paginationData);
      }
    } catch {}
  }, []);

  useDebounce(() => {
    if (searchValue.trim()) {
      loadPermissions(undefined, searchValue);
    } else {
      loadPermissions();
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (currentPageListNumber) {
      loadPermissions(currentPageListNumber);
    }
  }, [currentPageListNumber]);

  const renderEditButton = (permission: Permission): JSX.Element => {
    return (
      <IconButton
        color="inherit"
        onClick={() => {
          setSelectedPermission(permission);
          history.push('permissions/edit');
        }}
      >
        <Edit />
      </IconButton>
    );
  };

  const renderContent = (paginationData: Permission[]): JSX.Element => {
    return (
      <>
        {paginationData.map((permission) => (
          <TableRow key={permission.id}>
            <TableCell>{permission.name}</TableCell>
            <TableCell className="unique-value-cell">{permission.code}</TableCell>
            <TableCell>
              {permission.roles && permission.roles.length > 0
                ? permission.roles.map((role) => (
                    <Chip key={role.id} size="small" variant="outlined" label={role.description} />
                  ))
                : '-'}
            </TableCell>
            <TableCell>{renderEditButton(permission)}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <SearchBar
        label={t('search-label')}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onClear={() => {
          setSearchValue('');
        }}
      />

      <TableContainer className="table-container" component={Paper}>
        <Table>
          <TableHead className="table-header">
            <TableRow>
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('code')}</TableCell>
              <TableCell>{t('permissions.roles-label')}</TableCell>
              <TableCell>{t('edit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginationPermission?.data && paginationPermission?.data.length > 0 ? (
              renderContent(paginationPermission.data)
            ) : (
              <TableRow>
                <TableCell component="th" scope="row">
                  {t('listPermissions-page-noPermissions')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginationPermission && (
        <TablePagination
          component="div"
          count={paginationPermission.total}
          page={paginationPermission.currentPage - 1}
          onPageChange={(_, page: number) => {
            setCurrentPageListNumber(page + 1);
            loadPermissions(page + 1);
          }}
          rowsPerPage={paginationPermission.perPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({ from, to, count }) => {
            return `${from}-${to} ${t('pagination-of')} ${count !== -1 ? count : '0'}`;
          }}
        />
      )}
    </>
  );
};
